import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Dialog,
  Box,
  Card,
  CardContent,
  Link,
  Chip,
  FormControl,
  Grid,
  IconButton,
  SvgIcon,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Container,
  useMediaQuery,
  CircularProgress,
  Drawer,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TypeAhead from 'components/TypeAhead';
import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, register, watch, Controller } from 'react-hook-form';
import FormProvider from 'hooks/form/form-provider';
import { changeNameFormat, allCapitalize } from 'utils/Utils';
import { CandidateEducationSchema } from 'models/CandidateProfileSchema';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import { updateCandidateCertificate } from 'api/users';
import dayjs from 'dayjs';
import { endpoints } from 'utils/axios';
import RemoveCertificate from './RemoveCertificateDialog';
import AddIcon from '@mui/icons-material/Add';
import AddSkills from './AddSkills';
import { deleteCertificateHistory } from 'api/users';
import Overlapping from './Overlapping';
import { certDefSkill } from 'data/language';
import { useTheme } from '@mui/material/styles';
import { EditSvgIcon, DeleteSvg } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

/**
 * CertificationDetails component
 * @param {object} certificateData - certification data
 * @param {function} onCancel - cancel function
 * @param {string} id - certification id
 * @param {function} onCertificateDataChange - certification data change function
 * @param {array} certificateInstituteData - certification institute data
 * @param {array} certificateTitleData - certification title data
 * @param {function} checkingOverlapping - function to check overlapping
 * @param {boolean} showCert - show certification flag
 * @param {function} handleShowCert - show certification function
 * @param {function} handleDeleteEdu - delete education function
 * @param {function} handleArrowClick - arrow click function
 * @param {boolean} isReadOnly - read only flag
 * @param {function} setReadOnly - set read only function
 * @returns {React.ReactElement} - CertificationDetails component
 */
const CertificationDetails = ({ certificateData, onCancel, id, onCertificateDataChange, certificateInstituteData, certificateTitleData, checkingOverlapping, showCert, handleShowCert, handleDeleteEdu, handleArrowClick, isReadOnly, setReadOnly }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { user } = useAuth();
  const [showFlag, setShowFlag] = useState('');
  const [selectedChips, setSelectedChips] = useState(certificateData?.skills || []);
  const [educationFrom, setEducationFrom] = useState(dayjs(certificateData?.education_from) || null);
  const [educationTo, setEducationTo] = useState(dayjs(certificateData?.education_to) || null);
  const focusRef = useRef(null);
  const certificateInteractionFlag = useRef(false);
  // const [open, setOpen] = useState(false);
  // const [openRemove, setOpenRemove] = useState(false);
  const [addMoreSkills, setAddMoreSkills] = useState(certificateData?.all_skills || []);
  const [chipData, setChipData] = useState(certificateData?.skills?.length > 2 ? certificateData?.skills : certificateData?.all_skills || []);

  const defaultValues = {
    education_from: null,
    education_to: null,
    education_institute_name: '',
    certificate_received: '',
    learning_format: 'Self Study',
    field_of_study: '',
    skills: [],
    all_skills: [],
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(CandidateEducationSchema('certificate', user?.date_of_birth)),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty, isValid, dirtyFields, isSubmitting },
    watch,
    trigger,
  } = methods;

  const watchFileds = watch();

  useLayoutEffect(() => {
    if (!isEditMode) focusRef.current?.focus()
  }, []);

  // const [openOverlapping, setOpenOverlapping] = useState(false);
  const [type, setType] = useState('');
  const [degree, setDegree] = useState('');
  const [newCertificationData, setNewCertificationData] = useState(null);


  /**
   * Checks if the given data object is empty based on specified meaningful fields.
   * A field is considered meaningful if it is non-null, non-undefined, non-empty string, or non-empty array.
   * The function returns true if none of the meaningful fields contain meaningful data.
   *
   * @param {Object} data - The data object to check for meaningful content.
   * @returns {boolean} - True if all specified fields are empty, false otherwise.
   */

  const checkLocalDataIsEmpty = (data) => {
    if (!data) return true;

    // Define the specific fields that indicate a user has started filling out the form.
    const meaningfulFields = [
      'education_institute_name',
      'certificate_received',
      'field_of_study',
      'education_from',
      'education_to',
      'skills'
    ];

    // Use .some() to see if even ONE field has meaningful data.
    const hasMeaningfulData = meaningfulFields.some(field => {
      const value = data[field];

      if (value === null || value === undefined) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.filter(item => String(item).trim() !== '').length > 0;
      }
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return true;
    });

    return !hasMeaningfulData;
  };

  useEffect(() => {
    if (isEditMode) return;
    const subscription = watch((formValues) => {
      if (checkLocalDataIsEmpty(formValues)) {
        localStorage.removeItem('certificate');
      } else {
        localStorage.setItem('certificate', JSON.stringify(formValues));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isEditMode]);

  const handleReset = (data) => {
    const dataToReset = data || defaultValues;
    reset(dataToReset);
    setChipData(dataToReset?.skills?.length > 2 ? dataToReset?.skills : dataToReset?.all_skills || []);
  };

  useEffect(() => {
    if (certificateData) {
      handleReset(certificateData);
    } else {
      const savedData = JSON.parse(localStorage.getItem('certificate'));
      handleReset(savedData);
    }
    certificateInteractionFlag.current = false;
  }, [certificateData]);


  const handleEducationFromChange = async (date) => {
    if (date) {
      const firstDayOfMonth = date.startOf('month');
      setEducationFrom(firstDayOfMonth);
      setValue('education_from', firstDayOfMonth?.$d);
      trigger('education_from');
    }
  };

  /**
   * Handles the change event of the 'To' date input element.
   * If the user selects a date, it sets the value of 'education_to' to the last day of the selected month
   * and triggers the 'education_to' field validation.
   * @param {Date} date - the selected date
   */
  const handleEducationtoChange = (date) => {
    if (date) {
      const lastDayOfMonth = date.endOf('month');
      setEducationTo(lastDayOfMonth);
      setValue('education_to', lastDayOfMonth?.$d);
      trigger('education_to');
    }
  };

  const format = [{ label: 'Full time - offline' }, { label: 'Full time - online' }, { label: 'Part time - offline' }, { label: 'Part time - online' }, { label: 'Self Study' }];

  const onSubmit = handleSubmit(async (formData) => {
    const finalFormData = { ...formData };
    if (isEditMode) finalFormData.certificationId = id;

    if (!validateSkills(finalFormData.skills)) return;
    // formData.skills = selectedChips;
    // formData.all_skills = chipData;
    const { isOverlapping, type, degree } = checkingOverlapping(finalFormData);

    if (isOverlapping) {
      if (!finalFormData.is_overlap) {
        finalFormData.is_overlap = true;
        setNewCertificationData(finalFormData);
        setDegree(degree);
        setType(type);
        handleOpenOverlapping();
        return;
      }
    } else {
      finalFormData.is_overlap = false;
    }

    if (showCert) {
      handleShowCert(finalFormData, id);
      handleReset(defaultValues);
      setReadOnly(true);
      return;
    }
    await updateCertification(finalFormData);
  });

  /**
   * Updates a candidate's certificate details.
   * @param {Object} data - The object containing the updated certificate details.
   * @returns {Promise} - A promise that resolves with the response from the server after updating the certificate.
   */
  const updateCertification = async (data) => {
    if (data) {
      const response = await updateCandidateCertificate(data);
      if (response?.success === true && response.data) {
        const certificateId = response?.data?._id;
        if (response?.removedWorkGapResult)
          enqueueSnackbar("Career break has been removed.", { variant: 'success' });
        if (!isEditMode) {
          localStorage.removeItem('certificate');
        }
        if (isEditMode) onCertificateDataChange(true, certificateId);
        else onCertificateDataChange(true);
        onCancel();
      } else if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    }
  };

  /**
   * Handles the selection of skills. If the label is already selected, it
   * removes it from the list of skills. If the label is not selected and the
   * length of the skills list is less than 8, it adds the label to the list.
   * If the length of the skills list is 8 or more, it shows an error message.
   * @param {string} label - The label of the skill to be selected or deselected.
   */
  const handleClick = (label) => {
    const currentSkills = getValues('skills') || [];
    let newSkills;
    if (currentSkills.includes(label)) {
      newSkills = currentSkills.filter((chip) => chip !== label);
    } else {
      if (currentSkills.length >= 8) {
        setShowFlag('You can select up to 8 skills.');
        return;
      }
      newSkills = [...currentSkills, label];
    }
    setValue("skills", newSkills, { shouldDirty: true });
    trigger("skills");
    validateSkills(newSkills);
  };

  const validateSkills = (totalSkillls) => {
    if (totalSkillls?.length < 3) {
      setShowFlag('Please select at least 3 skills');
      return false;
    } else if (totalSkillls?.length > 8) {
      setShowFlag('You can select up to 8 skills.');
      return false;
    }
    setShowFlag('');
    return true;
  };

  /**
   * Opens the OTP dialog box.
   * @function
   */
  // const handleOpenOtpDialog = () => {
  //   setOpen(true);
  // };

  /**
   * Closes the OTP and Remove dialogs.
   * @function
   */
  // const handleClose = () => {
  // setOpen(false);
  //   setOpenRemove(false);
  // };

  /**
   * Updates the core skills and selected chip data with the new set of skills provided.
   * Also validates the skills and filters out any skills that are not in the new set.
   * @param {Array<string>} moreSkills - The new set of skills to add to the core skills and selected chip data.
   */
  const handleAddMoreSkills = (moreSkills) => {
    validateSkills(moreSkills);
    if (moreSkills?.length > 0) {
      setValue("skills", moreSkills, { shouldDirty: true });
      setValue("all_skills", moreSkills, { shouldDirty: true });
      trigger("skills");
      setChipData(moreSkills);
    }
  };

  /**
   * Handles the deletion of a certificate. If the certificateId is null, removes the certificate from local storage and calls the onCancel function. If the certificateId is not null, opens the Remove dialog box.
   * @param {string} certificateId - The id of the certificate to be deleted.
   * @param {string} title - The title of the certificate to be deleted.
   * @param {Event} event - The event object.
   */
  const handleDeleteCertificate = async (certificateId, title, event) => {
    event.stopPropagation();
    if (!certificateId) {
      localStorage.removeItem('certificate');
      onCancel();
    } else {
      handleOpenRemove();
    }
  };
  /**
   * Handles the deletion of a certificate. If the showCert flag is true, calls the handleDeleteEdu function and closes the dialog. Otherwise, calls the deleteCertificateHistory function and closes the dialog if the response is successful.
   */
  const handleDeleteAction = async () => {
    if (showCert) {
      handleDeleteEdu(certificateData._id);
      handleCloseRemove();
      return;
    }
    const response = await deleteCertificateHistory({ certificateId: certificateData._id });
    if (response && response?.success) {
      onCertificateDataChange(true);
    } else {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
    handleCloseRemove();
  };
  /**
   * Handles the confirmation of the add or edit certification dialog.
   * If showCert is true, calls the handleShowCert function and sets the read-only flag to true.
   * If showCert is false, calls the updateCertification function with the new certificate data.
   * @param {Object} newCertificationData - The new certificate data to be saved.
   */
  const handleConfirm = async (newCertificationData) => {
    if (showCert) {
      handleShowCert(newCertificationData, id);
      setReadOnly(true);
      return;
    }
    await updateCertification(newCertificationData);
  };

  /**
   * Cancels the add or edit certification dialog. If there is no id (i.e., the dialog is for adding a new certification), removes the certificate from local storage. Calls the onCancel function to close the dialog.
   * @function
   */
  const handleCancel = () => {
    if (!isEditMode) {
      localStorage.removeItem('certificate');
      handleReset(defaultValues);
    }
    onCancel();
  };

  /**
   * Resets the form data to the original certificate data when the user collapses the certification section in edit mode.
   * Calls the handleArrowClick function to navigate back to the certification list.
   * If the showCert flag is true, sets the read-only flag to true to prevent further editing of the certification.
   * @function
   */
  const handleCollapse = () => {
    if (isEditMode) {
      handleReset(certificateData);
    }
    handleArrowClick();
    if (showCert) setReadOnly(true);
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openSkillDrawer = dialogIds.includes("moreSkills");
  const openOverlapping = dialogIds.includes("overlapping");
  const openRemove = dialogIds.includes("removeExpandCertificate");

  const handleCloseSkillDrawer = () => {
    closeDialog('moreSkills');
  };
  const handleOpenSkillDrawer = () => {
    openDialog('moreSkills');
  };
  /**
   * Closes the overlapping dialog box.
   * @function
   */
  const handleCloseOverlapping = () => {
    closeDialog('overlapping');
  };
  const handleOpenOverlapping = () => {
    openDialog('overlapping');
  };
  const handleCloseRemove = () => {
    closeDialog('removeExpandCertificate');
  };
  const handleOpenRemove = () => {
    openDialog('removeExpandCertificate');
  };

  if (!certificateInstituteData || !certificateInstituteData?.length > 0 || !certificateTitleData || !certificateTitleData?.length) {
    return (
      <div key="add_edit_edu">
        <Box className="candidate-basic-det-wrapper"></Box>
        <Card className="form-wrapper" variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} mb={3}>
              <CircularProgress />
            </Stack>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <Box className="candidate-basic-det-wrapper education-details-form">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Tooltip title={isReadOnly ? "Any information updated after unlocking will be reflected across resumes" : ''} arrow followCursor placement='top'>
          {!isMobile && <Box className="details-info-header" mb={2.5}>
            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} spacing={2}>
              <Box>
                <Stack direction="row" alignItems={'center'} spacing={1.5}>
                  <Typography variant="h6" className="card-header-title">
                    {watch('certificate_received') ? (`${watch('certificate_received')} `) + `at ` : ''}
                    {watch('education_institute_name') ? allCapitalize(watch('education_institute_name')) : 'Certification Details'}
                  </Typography>
                </Stack>
              </Box>
              <Stack direction="row" alignItems={'center'} spacing={2} >
                {certificateData && !isMobile && (
                  !isReadOnly ? <IconButton
                    className="delete-sec-btn"
                    aria-label="delete"
                    onClick={(event) =>
                      handleDeleteCertificate(certificateData?._id, certificateData?.degree_received, event)
                    }
                  >
                    <DeleteSvg />
                  </IconButton> :
                    <IconButton aria-label="delete" sx={{ padding: 0, marginRight: 1 }} className="edit-icon" disableRipple onClick={() => setReadOnly(false)}>
                      <EditSvgIcon />
                    </IconButton>
                )}
                {!isMobile && <KeyboardArrowUpOutlinedIcon
                  onClick={handleCollapse}
                  style={{ cursor: 'pointer' }}
                />}
              </Stack>
            </Stack>
          </Box>
          }
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              {/* institute name */}
              <FormControl fullWidth size="small">
                <Controller
                  control={control}
                  name="education_institute_name"
                  defaultValue={[defaultValues?.education_institute_name || '']}
                  render={({ field }) => (
                    <TypeAhead
                      {...field}
                      ref={focusRef}
                      data={certificateInstituteData}
                      multiple={false}
                      fullWidth
                      label="Institution"
                      placeholder="Institution Name"
                      freeSolo={true}
                      onInputChange={(e, value) => field.onChange(value)}
                      disabled={isReadOnly}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* certificate received */}
              <FormControl fullWidth size="small">
                <Controller
                  control={control}
                  name="certificate_received"
                  defaultValue={defaultValues?.certificate_received || ''}
                  render={({ field }) => (
                    <TypeAhead
                      {...field}
                      data={certificateTitleData}
                      label="Certification Received"
                      displayTextKey="name"
                      freeSolo={true}
                      onInputChange={(e, value) => {
                        field.onChange(value);
                        if (!certificateInteractionFlag.current && value) {
                          setValue("all_skills", certDefSkill, { shouldDirty: true });
                          setChipData(certDefSkill);
                          certificateInteractionFlag.current = true;
                        }
                      }}
                      onChange={(e, value) => {
                        field.onChange(value?.name || value);
                        const newAvailableSkills = value?.skills || certDefSkill;
                        setValue('all_skills', newAvailableSkills, { shouldDirty: true });
                        setChipData(newAvailableSkills);
                        setValue("skills", [], { shouldDirty: true });
                        trigger("skills");
                      }}
                      disabled={isReadOnly}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Specialization */}
              <FormControl fullWidth size="small">
                <Controller
                  control={control}
                  name="field_of_study"
                  defaultValue={defaultValues?.field_of_study || []}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      {...field}
                      {...register('field_of_study')}
                      label="Specialization"
                      variant="outlined"
                      fullWidth
                      error={!!errors.field_of_study}
                      helperText={errors.field_of_study ? errors.field_of_study.message : ''}
                      onChange={(e) =>
                        setValue("field_of_study", e.target.value, { shouldDirty: true })
                      }
                      disabled={isReadOnly}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* learning format */}
              <FormControl fullWidth size="small">
                <InputLabel id="learning_format">Learning Format</InputLabel>
                <Controller
                  control={control}
                  name="learning_format"
                  defaultValue={defaultValues?.learning_format || ''}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="learning_format"
                      error={!!errors.learning_format}
                      helperText={errors.learning_format?.message}
                      label="Learning Format"
                      variant="outlined"
                      fullWidth
                      disabled={isReadOnly}
                    >
                      {format?.map((item) =>
                        <MenuItem value={item?.label}>{item?.label}</MenuItem>
                      )}
                    </Select>
                  )}
                />
                <Typography variant="caption" color="error">
                  {errors.learning_format?.message}
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* start date */}
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="education_from"
                  defaultValue={defaultValues?.education_from}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={dayjs(field?.value)}
                      format="MM/YYYY"
                      views={['year', 'month']}
                      label="Start Date"
                      disableFuture
                      placeholderText="Start Date"
                      onChange={(date) => handleEducationFromChange(date)}
                      closeOnSelect={true}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!errors?.education_from,
                          helperText: errors?.education_from?.message,
                        },
                      }}
                      disabled={isReadOnly}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* end date */}
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="education_to"
                  defaultValue={defaultValues?.education_to}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={dayjs(field?.value)}
                      autoOk={true}
                      format="MM/YYYY"
                      views={['year', 'month']}
                      label="End Date"
                      onChange={(date) => handleEducationtoChange(date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!errors?.education_to,
                          helperText: errors?.education_to?.message,
                        },
                      }}
                      disableFuture
                      closeOnSelect={true}
                      disabled={isReadOnly}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Box className="besic-det-skills-wrapper">
            <Box mb={2}>
              <Typography className="title" variant="body2" gutterBottom>
                Skills
              </Typography>
              <Typography className="info-txt" variant="body2" gutterBottom>
                Minimum 3 and maximum 8 skills to be selected
              </Typography>
            </Box>
            <Stack direction="row" flexWrap="wrap">
              {chipData?.map((label, index) => {
                const selectedSkills = watch('skills', []);
                const isSelected = selectedSkills.some(
                  (skill) => skill.toLowerCase() === label.toLowerCase()
                );

                return (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    clickable
                    variant={isSelected ? 'filled' : 'outlined'}
                    style={{
                      color: isSelected ? 'white' : 'black',
                      backgroundColor: isSelected ? '#ade9ab' : 'transparent',
                      border: isSelected ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                    }}
                    onClick={() => handleClick(label)}
                    tabIndex={-1}
                    disabled={isReadOnly}
                  />
                );
              })}
            </Stack>

            {(errors?.skills || showFlag) && (
              <Typography variant="caption" color="error" style={{ color: 'red', fontSize: 'small' }}>
                {errors.skills?.message || showFlag}<br />
              </Typography>
            )}
            {!isReadOnly && <Link size="small" className="add-more-skills-link" underline="none" onClick={handleOpenSkillDrawer} tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleOpenSkillDrawer();
                }
              }}
            >
              Add More Skills
            </Link>}
          </Box>
        </Tooltip>
        {!isReadOnly && <Box className="form-action-btn">
          <Stack direction="row" spacing={{ xs: 0, md: 2 }} mt={{ xs: 0, md: 3 }}>
            <Button variant="text" className="cancel-btn" sx={{ display: { xs: 'none', md: 'block' } }} size="large" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" className="done-btn" size="large" type="submit" disabled={isSubmitting} >
              {isEditMode ? (isSubmitting ? 'Updating...' : 'Done') : (isSubmitting ? 'Saving...' : 'Done')}
            </Button>
          </Stack>
        </Box>}

        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openRemove}
            onClose={handleCloseRemove}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <RemoveCertificate
              onClose={handleCloseRemove}
              onRemove={handleDeleteAction}
              title={certificateData?.certificate_received}
            />
          </Drawer> :
          <Dialog
            open={openRemove}
            onClose={handleCloseRemove}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <RemoveCertificate
              onClose={handleCloseRemove}
              onRemove={handleDeleteAction}
              title={certificateData?.certificate_received}
            />
          </Dialog>}
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />
          </Drawer> :
          <Dialog
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />
          </Dialog>}
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newCertificationData)}
              type={type}
              degree={degree} />
          </Drawer> :
          <Dialog
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newCertificationData)}
              type={type}
              degree={degree} />
          </Dialog>}
      </FormProvider>
    </Box>
  );
};

export default CertificationDetails;
